import type { ApiClient } from "../../api";
import {
  deleteApiV1SiteCategoriesByCategory,
  deleteApiV1SiteMediaByMedia,
  deleteApiV1SiteNewsByNews,
  deleteApiV1SitePagesByPage,
  getApiV1SiteCategories,
  getApiV1SiteNews,
  getApiV1SiteNewsByNews,
  getApiV1SiteNewsByNewsPreview,
  getApiV1SitePages,
  getApiV1SitePagesByPage,
  getApiV1SiteSettings,
  patchApiV1SiteCategoriesByCategory,
  patchApiV1SiteNewsByNews,
  patchApiV1SitePagesByPage,
  postApiV1SiteCacheInvalidate,
  postApiV1SiteCategories,
  postApiV1SiteMedia,
  postApiV1SiteNews,
  postApiV1SiteNewsByNewsPublish,
  postApiV1SiteNewsByNewsSchedule,
  postApiV1SiteNewsByNewsUnpublish,
  postApiV1SitePages,
  putApiV1SiteSettings,
  type GetApiV1SiteNewsQuery,
  type GetApiV1SiteNewsSuccess,
  type GetApiV1SitePagesSuccess,
  type GetApiV1SiteCategoriesSuccess,
  type GetApiV1SiteSettingsSuccess,
  type PatchApiV1SiteCategoriesByCategoryBody,
  type PatchApiV1SiteNewsByNewsBody,
  type PatchApiV1SitePagesByPageBody,
  type PostApiV1SiteCategoriesBody,
  type PostApiV1SiteMediaBody,
  type PostApiV1SiteNewsBody,
  type PostApiV1SiteNewsByNewsScheduleBody,
  type PostApiV1SitePagesBody,
  type PutApiV1SiteSettingsBody,
} from "../../api/generated";
import { FilesApi } from "../files";
import type { SignedUpload } from "../files";

export type SiteContentNews = GetApiV1SiteNewsSuccess["data"][number];
export type SiteContentNewsQuery = NonNullable<GetApiV1SiteNewsQuery>;
export type SiteContentCategory = GetApiV1SiteCategoriesSuccess["data"][number];
export type SiteContentPage = GetApiV1SitePagesSuccess["data"][number];
export type SiteContentSettings = GetApiV1SiteSettingsSuccess["data"];
// Короткие имена оставлены для административных экранов модуля.
export type SiteCategory = SiteContentCategory;
export type SitePage = SiteContentPage;
export type SiteSettings = SiteContentSettings;
export type CreateSiteNewsInput = PostApiV1SiteNewsBody;
export type UpdateSiteNewsInput = PatchApiV1SiteNewsByNewsBody;
export type ScheduleSiteNewsInput = PostApiV1SiteNewsByNewsScheduleBody;
export type CreateSiteCategoryInput = PostApiV1SiteCategoriesBody;
export type UpdateSiteCategoryInput = PatchApiV1SiteCategoriesByCategoryBody;
export type CreateSitePageInput = PostApiV1SitePagesBody;
export type UpdateSitePageInput = PatchApiV1SitePagesByPageBody;
export type UpdateSiteSettingsInput = PutApiV1SiteSettingsBody;
export type CreateSiteMediaInput = PostApiV1SiteMediaBody;

/** Типизированная граница административного модуля Site Content. */
export class SiteContentApi {
  private readonly files: FilesApi;

  constructor(private readonly client: ApiClient) {
    this.files = new FilesApi(client);
  }

  listNews(query: SiteContentNewsQuery = {}) {
    return getApiV1SiteNews(this.client, { query });
  }

  news(id: string) {
    return getApiV1SiteNewsByNews(this.client, { path: { news: id } });
  }

  previewNews(id: string) {
    return getApiV1SiteNewsByNewsPreview(this.client, { path: { news: id } });
  }

  createNews(input: CreateSiteNewsInput) {
    return postApiV1SiteNews(this.client, { body: input });
  }

  updateNews(id: string, input: UpdateSiteNewsInput) {
    return patchApiV1SiteNewsByNews(this.client, {
      path: { news: id },
      body: input,
    });
  }

  publishNews(id: string) {
    return postApiV1SiteNewsByNewsPublish(this.client, { path: { news: id } });
  }

  scheduleNews(id: string, input: ScheduleSiteNewsInput) {
    return postApiV1SiteNewsByNewsSchedule(this.client, {
      path: { news: id },
      body: input,
    });
  }

  unpublishNews(id: string) {
    return postApiV1SiteNewsByNewsUnpublish(this.client, {
      path: { news: id },
    });
  }

  deleteNews(id: string) {
    return deleteApiV1SiteNewsByNews(this.client, { path: { news: id } });
  }

  listCategories() {
    return getApiV1SiteCategories(this.client);
  }

  categories() {
    return this.listCategories();
  }

  createCategory(input: CreateSiteCategoryInput) {
    return postApiV1SiteCategories(this.client, { body: input });
  }

  updateCategory(id: string, input: UpdateSiteCategoryInput) {
    return patchApiV1SiteCategoriesByCategory(this.client, {
      path: { category: id },
      body: input,
    });
  }

  deleteCategory(id: string) {
    return deleteApiV1SiteCategoriesByCategory(this.client, {
      path: { category: id },
    });
  }

  listPages() {
    return getApiV1SitePages(this.client);
  }

  pages() {
    return this.listPages();
  }

  page(id: string) {
    return getApiV1SitePagesByPage(this.client, { path: { page: id } });
  }

  createPage(input: CreateSitePageInput) {
    return postApiV1SitePages(this.client, { body: input });
  }

  updatePage(id: string, input: UpdateSitePageInput) {
    return patchApiV1SitePagesByPage(this.client, {
      path: { page: id },
      body: input,
    });
  }

  deletePage(id: string) {
    return deleteApiV1SitePagesByPage(this.client, { path: { page: id } });
  }

  settings() {
    return getApiV1SiteSettings(this.client);
  }

  updateSettings(input: UpdateSiteSettingsInput) {
    const { hero_slides: slides, ...settings } = input.settings;
    return putApiV1SiteSettings(this.client, {
      body: {
        settings: {
          ...settings,
          ...(slides
            ? {
                hero_slides: slides.map((slide) => {
                  const { image_url, ...input } = slide;
                  void image_url;

                  return input;
                }),
              }
            : {}),
        },
      },
    });
  }

  invalidateCache() {
    return postApiV1SiteCacheInvalidate(this.client);
  }

  createMedia(input: CreateSiteMediaInput) {
    return postApiV1SiteMedia(this.client, { body: input });
  }

  uploadObject(upload: SignedUpload, body: Blob): Promise<void> {
    return this.files.uploadObject(upload, body);
  }

  confirmMedia(fileId: string) {
    return this.files.confirm(fileId);
  }

  /** Создаёт media record, загружает байты в Garage и подтверждает общий file record. */
  async uploadMedia(input: CreateSiteMediaInput, body: Blob) {
    const media = await this.createMedia(input);
    await this.uploadObject(media.upload, body);
    await this.confirmMedia(media.data.file_id);
    // До публикации у медиа только защищённый предпросмотр. Размеры
    // проверяются очередью, публичный адрес появится после публикации.
    return {
      ...media.data,
      url: media.data.url,
      preview_url: `/api/v1/site/media/${media.data.id}/content`,
    };
  }

  deleteMedia(id: string) {
    return deleteApiV1SiteMediaByMedia(this.client, { path: { media: id } });
  }
}
